import { Box, Flex, Text, Circle } from '@chakra-ui/react';
import { useMemo } from 'react';
import { Button } from 'src/components/atoms/Button';

const steps = [
  { label: 'Create Account' },
  { label: 'Connect your channels' },
  { label: 'Admit Data' },
  { label: 'Start Modeling' },
  { label: 'Invite your colleagues' },
];

type StepperSidebarProps = {
  currentStep: number;
  onNext?: () => void;
  onBack?: () => void;
  isNextDisabled?: boolean;
  isBackDisabled?: boolean;
  isFormValid?: boolean;
};

export const StepperSidebar: React.FC<StepperSidebarProps> = ({
  currentStep,
  onNext,
  onBack,
  isNextDisabled,
  isBackDisabled,
  isFormValid = true,
}) => {
  const displayStep = useMemo(() => {
    // currentStep values and mapping:
    // 0 -> 0: Create Account -> Create Account (step 0 in sidebar)
    // 1 -> 1: Connect Channels -> Connect Channels (step 1 in sidebar)
    // 2 -> 2: Upload Data -> Admit Data (step 2 in sidebar)
    // 3 -> 2: Preparing Data -> Admit Data (step 2 in sidebar, collapsed)
    // 4 -> 2: Campaign Match -> Admit Data (step 2 in sidebar, collapsed)
    // 5 -> 3: Start Modeling -> Start Modeling (step 3 in sidebar)
    // 6 -> 4: Invite Colleagues -> Invite Colleagues (step 4 in sidebar)
    if (currentStep === 0) return 0; // Create Account
    if (currentStep === 1) return 1; // Connect Channels
    if (currentStep >= 2 && currentStep <= 4) return 2; // Upload, Preparing, Campaign Match -> Admit Data
    if (currentStep >= 5) return currentStep - 2; // Start Modeling and beyond (5->3, 6->4)
    return currentStep;
  }, [currentStep]);

  const buttonName = useMemo(() => {
    if (displayStep === 0) return 'Create account';
    return 'Next';
  }, [displayStep]);

  return (
    <Flex
      direction="column"
      p={6}
      h="100%"
      borderRadius="2xl"
      bg="surface.card"
      border="1.5px solid"
      borderColor="border.box">
      <Box flex={1}>
        {steps.map((step, idx) => {
          const isCompleted = idx < displayStep;
          const isCurrent = idx === displayStep;
          const isUpcoming = idx > displayStep;
          return (
            <Flex
              key={step.label}
              align="flex-start"
              minH="108px"
              position="relative">
              {/* Stepper line and circle */}
              <Flex
                w="50px"
                direction="column"
                align="center"
                position="relative">
                {/* Top line */}
                {idx > 0 && (
                  <Box
                    position="absolute"
                    top={0}
                    left="50%"
                    w="3px"
                    h="20px"
                    bg={isCompleted ? 'icon.green' : 'border.box'}
                    transform="translateX(-50%)"
                    zIndex={0}
                  />
                )}
                {/* Step circle */}
                <Circle
                  size="50px"
                  bg={isCompleted ? 'icon.green' : 'surface.card'}
                  color={
                    isCompleted
                      ? 'white'
                      : isCurrent
                        ? 'text.body'
                        : 'text.muted'
                  }
                  border={
                    isCurrent
                      ? '2px solid'
                      : isUpcoming
                        ? '2px solid'
                        : '2px solid'
                  }
                  borderColor={
                    isCurrent
                      ? 'icon.green'
                      : isUpcoming
                        ? 'border.box'
                        : 'icon.green'
                  }
                  fontWeight={700}
                  fontSize="20px"
                  zIndex={1}
                  transition="all 0.2s"
                  mb={1}>
                  {`0${idx + 1}`}
                </Circle>
                {/* Bottom line */}
                {idx < steps.length - 1 && (
                  <Box
                    position="absolute"
                    top="58px"
                    left="50%"
                    w="2px"
                    h="42px"
                    bg={idx < displayStep ? 'icon.green' : 'border.box'}
                    transform="translateX(-50%)"
                    zIndex={0}
                  />
                )}
              </Flex>
              {/* Step labels */}
              <Box ml={3} mt={1}>
                <Text
                  fontSize="14px"
                  color="text.muted"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing={0.5}
                  mb={0.5}>{`STEP 0${idx + 1}`}</Text>
                <Text
                  fontWeight={500}
                  color="text.title"
                  fontSize="16px"
                  mt={0}>
                  {step.label}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </Box>
      <Flex borderTop="1px solid" borderColor="border.box" pt={6} gap={4}>
        {displayStep !== 0 && displayStep !== 1 && (
          <Button
            flex={1}
            buttonName="Back"
            bg="surface.control"
            color="text.body"
            borderRadius="lg"
            py={3.5}
            fontWeight={600}
            fontSize="lg"
            boxShadow="none"
            _hover={{ bg: 'surface.controlHover' }}
            onClick={onBack}
            isDisabled={isBackDisabled}>
            Back
          </Button>
        )}
        <Button
          flex={1}
          buttonName={displayStep === 0 ? 'Create account' : 'Next'}
          bg="icon.green"
          color="white"
          borderRadius="lg"
          py={3.5}
          fontWeight={600}
          fontSize="lg"
          boxShadow="none"
          _hover={{ bg: 'icon.green', opacity: 0.9 }}
          onClick={onNext}
          isDisabled={displayStep === 0 ? !isFormValid : isNextDisabled}>
          {buttonName}
        </Button>
      </Flex>
    </Flex>
  );
};
